import { memo, useEffect, useRef } from 'react';

export interface PipMarker {
  startSec: number;
  endSec: number;
  index: number;
}

interface MaskMarker {
  startSec: number;
  endSec: number;
  index: number;
}

interface Props {
  totalDuration: number;
  viewStart: number;
  viewDuration: number;
  maskMarkers?: MaskMarker[];
  pipMarkers?: PipMarker[];
}

const NICE_STEPS = [
  { step: 0.1, subDiv: 2 },
  { step: 0.2, subDiv: 2 },
  { step: 0.5, subDiv: 5 },
  { step: 1, subDiv: 2 },
  { step: 2, subDiv: 2 },
  { step: 5, subDiv: 5 },
  { step: 10, subDiv: 5 },
  { step: 15, subDiv: 3 },
  { step: 30, subDiv: 3 },
  { step: 60, subDiv: 4 },
  { step: 120, subDiv: 4 },
  { step: 300, subDiv: 5 },
  { step: 600, subDiv: 5 },
  { step: 900, subDiv: 3 },
  { step: 1800, subDiv: 3 },
  { step: 3600, subDiv: 4 },
  { step: 7200, subDiv: 4 },
];

function getRulerStep(targetSec: number): { step: number; subDiv: number } {
  for (const s of NICE_STEPS) {
    if (s.step >= targetSec) return s;
  }
  return { step: Math.ceil(targetSec / 3600) * 3600, subDiv: 4 };
}

function formatRulerTime(sec: number, totalDur: number, step: number): string {
  if (sec < 0) sec = 0;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  const frac = Math.round((sec - Math.floor(sec)) * 10);
  const p = (n: number) => String(n).padStart(2, '0');

  if (step < 1) {
    return `${p(m)}:${p(s)}.${frac}`;
  }
  if (totalDur >= 3600 || h > 0) {
    return `${h}:${p(m)}:${p(s)}`;
  }
  return `${p(m)}:${p(s)}`;
}

export const TimeRulerCanvas = memo(function TimeRulerCanvas({ totalDuration, viewStart, viewDuration, maskMarkers = [], pipMarkers = [] }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sizeRef = useRef({ w: 0, h: 0 });

  const render = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const { w, h } = sizeRef.current;
    if (w <= 0 || h <= 0 || totalDuration <= 0) return;

    const dpr = window.devicePixelRatio || 1;
    const targetW = Math.floor(w * dpr);
    const targetH = Math.floor(h * dpr);
    if (canvas.width !== targetW || canvas.height !== targetH) {
      canvas.width = targetW;
      canvas.height = targetH;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const isLight = document.documentElement.getAttribute('data-mantine-color-scheme') === 'light' ||
                    document.documentElement.getAttribute('data-theme') === 'light';

    const majorColor = isLight ? 'rgba(0, 0, 0, 0.65)' : 'rgba(255, 255, 255, 0.65)';
    const tickColor = isLight ? 'rgba(0, 0, 0, 0.35)' : 'rgba(255, 255, 255, 0.35)';
    const minorTickColor = isLight ? 'rgba(0, 0, 0, 0.15)' : 'rgba(255, 255, 255, 0.15)';
    const bottomBorderColor = isLight ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)';

    ctx.beginPath();
    ctx.strokeStyle = bottomBorderColor;
    ctx.lineWidth = 1;
    ctx.moveTo(0, h - 0.5);
    ctx.lineTo(w, h - 0.5);
    ctx.stroke();

    const targetSec = (80 / w) * viewDuration;
    const { step, subDiv } = getRulerStep(targetSec);
    const subStep = step / subDiv;

    const tStart = Math.max(0, Math.floor(viewStart / subStep) * subStep);
    const tEnd = Math.min(totalDuration, viewStart + viewDuration);

    ctx.font = '10px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, monospace';
    ctx.textBaseline = 'top';

    for (let t = tStart; t <= tEnd + subStep * 0.5; t += subStep) {
      const x = Math.round(((t - viewStart) / viewDuration) * w);
      if (x < -20 || x > w + 20) continue;

      const isMajor = Math.abs(Math.round(t / subStep) % subDiv) === 0;

      ctx.beginPath();
      if (isMajor) {
        ctx.strokeStyle = tickColor;
        ctx.lineWidth = 1;
        ctx.moveTo(x + 0.5, h - 7);
        ctx.lineTo(x + 0.5, h - 1);
        ctx.stroke();

        ctx.fillStyle = majorColor;
        ctx.fillText(formatRulerTime(t, totalDuration, step), x + 3, 3);
      } else {
        ctx.strokeStyle = minorTickColor;
        ctx.lineWidth = 1;
        ctx.moveTo(x + 0.5, h - 4);
        ctx.lineTo(x + 0.5, h - 1);
        ctx.stroke();
      }
    }

    for (const p of pipMarkers) {
      const x = Math.round(((p.startSec - viewStart) / viewDuration) * w);
      if (x >= -4 && x <= w + 4) {
        ctx.fillStyle = '#ff79c6';
        ctx.beginPath();
        ctx.moveTo(x, 7);
        ctx.lineTo(x - 4, 1);
        ctx.lineTo(x + 4, 1);
        ctx.closePath();
        ctx.fill();
      }
    }

    for (const m of maskMarkers) {
      const x = Math.round(((m.startSec - viewStart) / viewDuration) * w);
      if (x >= -4 && x <= w + 4) {
        ctx.fillStyle = '#20c997';
        ctx.beginPath();
        ctx.moveTo(x, h - 1);
        ctx.lineTo(x - 4, h - 7);
        ctx.lineTo(x + 4, h - 7);
        ctx.closePath();
        ctx.fill();
      }
    }
  };

  const renderRef = useRef(render);
  renderRef.current = render;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const updateSize = () => {
      const rect = canvas.getBoundingClientRect();
      sizeRef.current = { w: Math.floor(rect.width), h: Math.floor(rect.height) };
      renderRef.current();
    };
    updateSize();
    const ro = new ResizeObserver(updateSize);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    render();
  }, [totalDuration, viewStart, viewDuration, maskMarkers, pipMarkers]);

  return <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block', pointerEvents: 'none' }} />;
});
